import { useEffect, useState } from "react";
import api from "@/api/client";
import { Wallet, CheckCircle2, Hourglass } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function StatusPill({ status }) {
  const cls = status === "paid"
    ? "bg-green-100 text-green-700 border-green-200"
    : "bg-amber-100 text-amber-700 border-amber-200";
  return (
    <span data-testid={`payout-pill-${status}`} className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${cls}`}>
      {status || "pending"}
    </span>
  );
}

export default function VendorPayouts({ kyc }) {
  const { t } = useSiteContent();
  const supportEmail = t("footer.support_email", "Email: popupbazaar@cityshor.com").split(":").pop().trim();
  const [data, setData] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/exhibitor/payouts"),
      api.get("/platform/bank-account").catch(() => ({ data: {} })),
    ])
      .then(([r, b]) => {
        setData(r.data);
        setBank(b.data && b.data.account_number ? b.data : null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-500">Loading payouts…</div>;
  if (!data) return null;

  const records = data.records || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div data-testid="vendor-payout-lifetime" className="p-4 bg-white border border-gray-200 rounded-2xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500">
            <Wallet className="h-3.5 w-3.5 text-[#F48E2E]" /> Lifetime earnings (after {data.commission_config?.slab1_pct ?? 18}–{data.commission_config?.slab2_pct ?? 20}%)
          </div>
          <div className="font-display text-2xl mt-1">{money(data.lifetime_earnings)}</div>
        </div>
        <div data-testid="vendor-payout-paid" className="p-4 bg-white border border-gray-200 rounded-2xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Paid out to date
          </div>
          <div className="font-display text-2xl mt-1 text-green-700">{money(data.paid_to_date)}</div>
        </div>
        <div data-testid="vendor-payout-pending" className="p-4 bg-white border border-gray-200 rounded-2xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500">
            <Hourglass className="h-3.5 w-3.5 text-amber-600" /> Current pending balance
          </div>
          <div className="font-display text-2xl mt-1 text-amber-700">{money(data.pending_balance)}</div>
        </div>
      </div>

      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <div className="flex flex-wrap justify-between gap-2 items-start mb-3">
          <div>
            <div className="font-medium text-sm">Bank account for payouts</div>
            <div className="text-xs text-gray-500">To change, contact support at {supportEmail} — details are re-verified for security.</div>
          </div>
          <div className="text-right text-xs">
            <div><span className="text-gray-500">Holder:</span> {kyc?.bank_holder_name || "Not set"}</div>
            <div className="font-mono-tab text-gray-700">{kyc?.bank_account_number ? `XXXX ${kyc.bank_account_number.slice(-4)}` : "Not set"}</div>
            <div className="font-mono-tab text-gray-500">{kyc?.bank_ifsc || ""}</div>
          </div>
        </div>
      </div>

      {bank && (
        <div className="p-5 rounded-2xl bg-[#FEF3E6] border border-[#F48E2E]/30" data-testid="cityshor-bank-card">
          <div className="flex flex-wrap justify-between gap-2 items-start">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#F48E2E] font-bold mb-1">Where to transfer funds</div>
              <div className="font-medium text-sm">CityShor settlement account</div>
              <div className="text-xs text-gray-600 mt-1">For any balance top-ups or dues, transfer to this account and share the UTR with support.</div>
            </div>
            <div className="text-right text-xs space-y-0.5">
              <div><span className="text-gray-500">Holder: </span><span className="font-medium">{bank.account_holder_name}</span></div>
              <div><span className="text-gray-500">Bank: </span><span className="font-medium">{bank.bank_name}</span></div>
              <div><span className="text-gray-500">Account: </span><span className="font-mono-tab font-medium">{bank.account_number}</span></div>
              <div><span className="text-gray-500">IFSC: </span><span className="font-mono-tab font-medium">{bank.ifsc}</span></div>
              {bank.updated_at && (
                <div className="text-[10px] text-gray-500 pt-1" data-testid="bank-vendor-last-updated">Last updated {new Date(bank.updated_at).toLocaleString("en-IN")}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto" data-testid="vendor-payout-history">
        <div className="p-4 border-b border-gray-100">
          <div className="font-medium">Payout History</div>
          <div className="text-xs text-gray-500">CityShor settles delivered sales every Monday for the previous week.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3">Settlement Week</th>
              <th className="p-3 text-right">Orders</th>
              <th className="p-3 text-right">Gross</th>
              <th className="p-3 text-right">Packaging</th>
              <th className="p-3 text-right">Commission</th>
              <th className="p-3 text-right">Returns</th>
              <th className="p-3 text-right">Refunds</th>
              <th className="p-3 text-right">Gateway</th>
              <th className="p-3 text-right">Shipping</th>
              <th className="p-3 text-right">Admin refund</th>
              <th className="p-3 text-right">Other</th>
              <th className="p-3 text-right">Net Payable</th>
              <th className="p-3">UTR / Reference</th>
              <th className="p-3">Date Paid</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={15} className="p-8 text-center text-gray-500">No settlements yet. Once CityShor closes your first weekly cycle it will appear here.</td></tr>
            ) : records.map((r) => (
              <tr key={r.id} data-testid={`vendor-payout-row-${r.id}`} className="border-t">
                <td className="p-3 font-medium">{r.week_label}</td>
                <td className="p-3 text-right font-mono-tab">{r.order_count}</td>
                <td className="p-3 text-right font-mono-tab">{money(r.gross_sales)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">+{money(r.packaging_charges_collected)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">−{money(r.commission_amount)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">−{money(r.returns_deducted)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">−{money(r.refunds_deducted)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">−{money(r.gateway_fee)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500" title="Shiprocket freight on orders where you offered free delivery">−{money(r.shipping_deducted || 0)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">−{money(r.admin_refund_deduction)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500" title={(r.other_deductions||[]).map(o=>`${o.label}: ${money(o.amount)}`).join(" · ")}>−{money(r.other_deductions_total)}</td>
                <td className="p-3 text-right font-mono-tab font-medium">{money(r.net_payable || r.net_payout)}</td>
                <td className="p-3 font-mono-tab text-xs">{r.utr || "-"}</td>
                <td className="p-3 text-xs">{r.paid_at || "-"}</td>
                <td className="p-3"><StatusPill status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
